/**
 * playBloomChime — synthesised 480-ms cinematic whoosh + chime that
 * preludes the BloomingFlowerOverlay. Optimised for mobile browsers:
 *
 *   • Lazy-creates a SHARED `AudioContext` and reuses it across calls,
 *     avoiding the "too many audio contexts" warning on Safari iOS.
 *   • Calls `ctx.resume()` after creation — required on iOS / Chrome
 *     mobile when the gesture context window is closing.
 *   • Honours `prefers-reduced-motion: reduce` and the user's mute toggle
 *     (`localStorage.bloom_chime_muted === '1'`) — silent by default for
 *     accessibility.
 *   • Lowers the master gain by 35 % on touch devices so it doesn't
 *     distort tiny built-in phone speakers.
 *
 * Must be invoked **inside a user-gesture handler** (e.g. an onClick) so
 * the gesture token unlocks audio playback on iOS Safari. Calling it from
 * an effect fired *after* the gesture frame closes will produce no sound.
 */
let _ctx = null;

const isTouch = () =>
  typeof window !== 'undefined' &&
  (navigator.maxTouchPoints > 0 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || ''));

const prefersReducedMotion = () => {
  try {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch { return false; }
};

const isMuted = () => {
  try { return localStorage.getItem('bloom_chime_muted') === '1'; }
  catch { return false; }
};

const getCtx = () => {
  if (_ctx && _ctx.state !== 'closed') return _ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  _ctx = new Ctx();
  return _ctx;
};

export function playBloomChime() {
  if (typeof window === 'undefined') return;
  if (isMuted() || prefersReducedMotion()) return;

  let ctx;
  try { ctx = getCtx(); } catch { return; }
  if (!ctx) return;

  // iOS Safari starts the context in 'suspended' state until a gesture.
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  try {
    const now = ctx.currentTime;
    const mobileScale = isTouch() ? 0.65 : 1;     // 35 % quieter on phones

    // ── Whoosh: filtered noise burst ────────────────────────────
    const whooshDur = 0.42;
    const bufferSize = Math.floor(ctx.sampleRate * whooshDur);
    const noiseBuf = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const noiseData = noiseBuf.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) noiseData[i] = (Math.random() * 2 - 1) * 0.5;
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'highpass';
    noiseFilter.frequency.setValueAtTime(200, now);
    noiseFilter.frequency.exponentialRampToValueAtTime(2400, now + whooshDur);
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.0001, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.18 * mobileScale, now + 0.05);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + whooshDur);
    noise.connect(noiseFilter).connect(noiseGain).connect(ctx.destination);
    noise.start(now);
    noise.stop(now + whooshDur);

    // ── Chime: two slow-attack sine partials (E5 + B5) ──────────
    const chimeStart = now + 0.18;
    const chimeDur = 0.30;
    [659.25, 987.77].forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, chimeStart);
      g.gain.exponentialRampToValueAtTime((idx === 0 ? 0.10 : 0.06) * mobileScale, chimeStart + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, chimeStart + chimeDur);
      osc.connect(g).connect(ctx.destination);
      osc.start(chimeStart);
      osc.stop(chimeStart + chimeDur);
    });
  } catch {
    /* silent — audio is best-effort, must never block the bloom */
  }
}

/**
 * User-controllable mute (binds to a settings toggle).
 */
export function setBloomChimeMuted(muted) {
  try { localStorage.setItem('bloom_chime_muted', muted ? '1' : '0'); }
  catch { /* ignore */ }
}
